'use client';

import { c as _c } from "react/compiler-runtime";
import debounce from 'es-toolkit/compat/debounce';
import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { usePathname } from 'next/navigation';
import { type DependencyList, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import sessionAtom from '@/state/session';
import { createLoadableAtom } from '@/utils/jotai-loadable';
import type { Atom } from 'jotai/vanilla';
import type { Loadable } from 'jotai/vanilla/utils/loadable';
export function usePrevious<T>(value: T) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef<T>(undefined);
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}
export function useLoadable(loadableAtom, defaultValue) {
  const $ = _c(5);
  const loadableValue = useAtomValue(loadableAtom);
  const [state, setState] = useState(defaultValue);
  let t0;
  if ($[0] !== loadableValue.data || $[1] !== loadableValue.state) {
    t0 = () => {
      if (loadableValue.state !== "hasData" || !loadableValue.data) {
        return;
      }
      setState(loadableValue.data);
    };
    $[0] = loadableValue.data;
    $[1] = loadableValue.state;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== loadableValue) {
    t1 = [loadableValue];
    $[3] = loadableValue;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
  return state;
}
export function useSessionAtomValue() {
  return useAtomValue(sessionAtom);
}
export function useUnwrappedValue(atom) {
  const $ = _c(2);
  let t0;
  if ($[0] !== atom) {
    t0 = unwrap(atom);
    $[0] = atom;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useAtomValue(t0);
}

/**
  Returns the last truthy value from a Jotai atom, persisting it even when the atom's
  current value becomes falsy. Useful for maintaining the last valid state.

  @param atom - The Jotai atom to track
  @returns The last truthy value from the atom
*/
export function useLastTruthyValue(atom) {
  const $ = _c(6);
  let t0;
  if ($[0] !== atom) {
    t0 = unwrap(atom);
    $[0] = atom;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const unwrappedAtom = t0;
  const currentValue = useAtomValue(unwrappedAtom);
  const [lastTruthyValue, setLastTruthyValue] = useState(currentValue);
  let t1;
  let t2;
  if ($[2] !== currentValue || $[3] !== lastTruthyValue) {
    t1 = () => {
      if (!currentValue || Object.is(currentValue, lastTruthyValue)) {
        return;
      }
      setLastTruthyValue(currentValue);
    };
    t2 = [currentValue, lastTruthyValue];
    $[2] = currentValue;
    $[3] = lastTruthyValue;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return lastTruthyValue;
}
export function useLoadableValue(atom) {
  const $ = _c(2);
  let t0;
  if ($[0] !== atom) {
    t0 = createLoadableAtom(atom);
    $[0] = atom;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const loadableAtom = t0;
  return useAtomValue(loadableAtom);
}
export function useEnsuredPath() {
  const path = usePathname();
  if (!path) {
    throw new Error("Invalid pathname");
  }
  return path;
}
type Function = (...args: any) => any;
type RestParameters<T> = T extends ((first: any, ...rest: infer R) => any) ? R : never;
type DebounceParams = RestParameters<typeof debounce>;

/**
  Creates a debounced callback that delays invoking func until after
  wait milliseconds have elapsed since the last time the debounced function was invoked.

  The callback will be memoized so that it only changes if one of the deps has changed.

  @param func The function to debounce.
  @param deps The dependency array.
  @param wait The number of milliseconds to delay.
  @param options The options object. (See es-toolkit.debounce docs).

  @returns - The memoized, debounced version of the callback.
*/
export function useDebouncedCallback<T extends Function>(func: T, deps: DependencyList, ...params: DebounceParams) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependency list is provided by caller
  return useCallback(debounce(func, ...params), deps);
}